/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import { MenuItem, TextField } from '@mui/material';
import { ModelStatus as ModelStatusEnum } from 'dashboard409a/pages/Workboard/common/types';
import { ModelsStatusOption, modelsStatusOptions } from '../ModelsTable.types';

interface ModelsStatusDropdownProps {
  currentStatus: ModelsStatusOption;
  onStatusChange?: (newStatus: string) => void;
  isEditing: boolean;
  onEditEnd: () => void;
  loading?: boolean;
}

const ModelsStatusDropdown = ({
  currentStatus,
  onStatusChange,
  isEditing,
  onEditEnd,
  loading = false,
}: ModelsStatusDropdownProps) => {
  const statusValue = currentStatus.value || '';
  const [selectedStatus, setSelectedStatus] = useState(statusValue);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Check if status is final and should be read-only
  const isFinalStatus = currentStatus.value === ModelStatusEnum.FINAL;

  useEffect(() => {
    setSelectedStatus(currentStatus.value || '');
  }, [currentStatus]);

  // Automatically open the dropdown when entering edit mode (only if not final status)
  useEffect(() => {
    if (isEditing && !isFinalStatus) {
      setDropdownOpen(true);
    }
  }, [isEditing, isFinalStatus]);

  const handleStatusChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    // Prevent changes if status is final
    if (isFinalStatus) {
      return;
    }

    const newStatus = event.target.value as string;
    setSelectedStatus(newStatus);

    if (onStatusChange) {
      onStatusChange(newStatus);
    }

    // Close the dropdown after selecting
    setDropdownOpen(false);
    onEditEnd();
  };

  const handleStatusClose = () => {
    setDropdownOpen(false);
    onEditEnd();
    setSelectedStatus(currentStatus.value || '');
  };

  const handleStatusOpen = () => {
    setDropdownOpen(true);
  };

  // Don't render dropdown if not editing or if status is final
  if (!isEditing || isFinalStatus) {
    return null;
  }

  return (
    <TextField
      value={selectedStatus}
      select
      fullWidth
      variant="standard"
      onChange={handleStatusChange}
      onClick={e => {
        e.stopPropagation();
      }}
      autoFocus
      size="small"
      disabled={loading}
      SelectProps={{
        open: dropdownOpen,
        onClose: handleStatusClose,
        onOpen: handleStatusOpen,
      }}>
      <MenuItem value="" disabled>
        {loading ? 'Loading...' : 'Select Status'}
      </MenuItem>
      {modelsStatusOptions.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

export { ModelsStatusDropdown };
